type Op<Data> = () => Promise<Data>;

/**
 * Error thrown when an operation is cancelled.
 */
export class OpQueueCancelationError extends Error {
  constructor(message = "Operation was cancelled") {
    super(message);
    this.name = "OpQueueCancelationError";
  }
}

interface QueuedOp<OpInfo, OpData> {
  id: number;
  op: Op<OpData>;
  opInfo: OpInfo;
  resolve: (data: OpData) => void;
  reject: (error: Error) => void;
  cancel: () => void;
}

/**
 * A queue of async operations that are executed serially.
 *
 * This is useful for ensuring that operations are executed in the order they are added,
 * and that they are executed one at a time.
 *
 * Also includes a cancel mechanism to cancel operations.
 * OpInfo may be used to identify an operation in the queue for cancellation.
 */
export class SerialOpQueue<OpInfo> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private queue: QueuedOp<OpInfo, any>[] = [];
  private isProcessing = false;
  private nextId = 1;
  private currentOp: QueuedOp<OpInfo, unknown> | null = null;

  /**
   * Queues an operation and returns a Promise that resolves when the operation completes.
   */
  queueOp<OpData>(
    op: Op<OpData>,
    opInfo: OpInfo,
  ): { cancel: () => void; id: number; promise: Promise<OpData> } {
    const id = this.nextId++;
    let resolve: (data: OpData) => void;
    let reject: (error: Error) => void;
    const promise = new Promise<OpData>((res, rej) => {
      resolve = res;
      reject = rej;
    });

    // cancel the operation + remove it from the queue
    const cancel = () => {
      if (this.currentOp?.id === id) {
        this.currentOp.reject(new OpQueueCancelationError());
        this.currentOp = null;
      } else {
        this.queue = this.queue.filter((q) => q.id !== id);
        reject(new OpQueueCancelationError());
      }
    };

    const queuedOp: QueuedOp<OpInfo, OpData> = {
      id,
      op,
      opInfo,
      resolve: resolve!,
      reject: reject!,
      cancel,
    };
    this.queue.push(queuedOp);

    this.kickQueue();

    return {
      id,
      promise,
      cancel,
    };
  }

  /**
   * Gets info of all operations, including the currently running one (if any) as the first item.
   */
  getOps(): { id: number; opInfo: OpInfo }[] {
    const ops: { id: number; opInfo: OpInfo }[] = [];
    if (this.currentOp) {
      ops.push({ id: this.currentOp.id, opInfo: this.currentOp.opInfo });
    }
    return ops.concat(this.queue.map((q) => ({ id: q.id, opInfo: q.opInfo })));
  }

  /**
   * Cancels a specific operation by ID, whether it's currently running or queued.
   */
  cancelOp(id: number): void {
    if (this.currentOp?.id === id) {
      this.currentOp.cancel();
      return;
    }
    const op = this.queue.find((q) => q.id === id);
    if (op) {
      op.cancel();
    }
  }

  /**
   * Cancels all operations, including the currently running one.
   */
  cancelAllOps(): void {
    if (this.currentOp) {
      this.currentOp.reject(new OpQueueCancelationError());
      this.currentOp = null;
    }
    for (const op of this.queue) {
      op.reject(new OpQueueCancelationError());
    }
    this.queue = [];
  }

  /**
   * Kicks the queue to process the next operation if not already processing.
   */
  private async kickQueue(): Promise<void> {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    while (this.queue.length > 0) {
      const next = this.queue.shift();
      if (!next) break;

      this.currentOp = next;
      try {
        const data = await next.op();
        next.resolve(data);
      } catch (error) {
        next.reject(error as Error);
      } finally {
        this.currentOp = null;
      }
    }

    this.isProcessing = false;
  }
}

/** Catch can discard promises canceled by the op queue */
export function allowOpQueueCancellations(error: unknown) {
  if (error instanceof OpQueueCancelationError) return;
  throw error;
}
